import { Add, Edit, Power, Settings } from "@mui/icons-material";
import { Box, Button, IconButton, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useState } from "react";

import { HeaderPage } from "../../components/Header/HeaderPage";
import PageWrapper from "../../components/PageWrapper";
import TableDataGrid from "../../components/TableDataGrid";
import Toast from "../../components/Toast";
import { findFee } from "../../helpers/acquirer";
import { formatTransactionFee } from "../../helpers/transaction";
import { usePaginatedData } from "../../hooks/usePaginatedData";
import { useToast } from "../../hooks/useToast";
import { Acquirer } from "../../interfaces/acquirer";
import { AcquirerEditModal } from "./AcquirerEditModal";
import { FeeEditModal } from "./FeeEditModal";

export default function AcquirerIntegration() {
    const [openModal, setOpenModal] = useState(false);
    const [openAcquirerModal, setOpenAcquirerModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState<Acquirer | null>(null);

    const { toast, closeToast, updateToast } = useToast();

    const { data, isLoading, setPage, meta, refresh } =
        usePaginatedData<Acquirer>(
            "/acquirers?type=gateway&include=fees,acquirerMetaData"
        );

    const handleEditFee = (row: Acquirer) => {
        setSelectedRow(row);
        setOpenModal(true);
    };

    const handleEditAcquirer = (row: Acquirer) => {
        setSelectedRow(row);
        setOpenAcquirerModal(true);
    };

    const handleCreateAcquirer = () => {
        setSelectedRow(null);
        setOpenAcquirerModal(true);
    };

    const handleEditSuccess = () => {
        refresh();

        updateToast({
            open: true,
            severity: "success",
            text: "Configurações salvas com sucesso!",
        });
    };

    const handleEditError = (data: any, error: string) => {
        updateToast({
            open: true,
            severity: "error",
            text: error,
        });
    };

    const columns: GridColDef<Acquirer>[] = [
        {
            field: "name",
            headerName: "Nome",
            flex: 1,
            sortable: false,
        },
        {
            field: "description",
            headerName: "Descrição",
            flex: 2,
            sortable: false,
        },
        {
            field: "active",
            headerName: "Status",
            flex: 1,
            sortable: false,
            valueFormatter(params) {
                return params.value ? "Ativo" : "Inativo";
            },
        },
        {
            field: "pix_fee",
            headerName: "Taxa PIX",
            sortable: false,
            flex: 1,
            renderCell: (params) => {
                const acquirer = findFee(params.row, "pix");
                return formatTransactionFee(
                    acquirer?.fee_percentage,
                    acquirer?.fixed_fee
                );
            },
        },
        {
            field: "card_fee",
            headerName: "Taxa Cartão",
            sortable: false,
            flex: 1,
            renderCell: (params) => {
                const acquirer = findFee(params.row, "card");
                return formatTransactionFee(
                    acquirer?.fee_percentage,
                    acquirer?.fixed_fee
                );
            },
        },
        {
            field: "actions",
            headerName: "Ações",
            sortable: false,
            disableExport: true,
            align: "right",
            headerAlign: "right",
            width: 120,
            renderCell: (params) => (
                <Box display="flex" gap={1}>
                    <Tooltip title="Editar Adquirente">
                        <IconButton
                            onClick={() => handleEditAcquirer(params.row)}
                            color="primary"
                            size="small"
                        >
                            <Edit />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Configurar Taxas">
                        <IconButton
                            onClick={() => handleEditFee(params.row)}
                            color="info"
                            size="small"
                        >
                            <Settings />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    return (
        <PageWrapper>
            <Box display="flex" justifyContent="space-between" mb={3}>
                <HeaderPage
                    title="Integrações"
                    subtitle="Gerencie as integrações com as adquirentes"
                    icon={<Power />}
                />

                <Box display="flex" alignItems="flex-end" gap={2}>
                    <Button
                        startIcon={<Add />}
                        variant="contained"
                        onClick={handleCreateAcquirer}
                    >
                        Nova Integração
                    </Button>
                </Box>
            </Box>

            <Box mt={5}>
                <div className="containerListTablePagesGlobal">
                    <TableDataGrid
                        rows={data}
                        columns={columns}
                        loading={isLoading}
                        hideColumnMenu
                        hideExports
                        hideFilter
                        paginationMode="server"
                        rowCount={meta.total}
                        page={meta.current_page - 1}
                        onPageChange={(newPage) => setPage(newPage + 1)}
                        pageSize={meta.per_page}
                    />
                </div>
            </Box>

            <AcquirerEditModal
                open={openAcquirerModal}
                onClose={() => {
                    setOpenAcquirerModal(false);
                    setSelectedRow(null);
                }}
                data={selectedRow}
                onSuccess={handleEditSuccess}
                onError={handleEditError}
            />

            <FeeEditModal
                open={openModal}
                onClose={() => {
                    setOpenModal(false);
                    setSelectedRow(null);
                }}
                data={selectedRow}
                onSuccess={handleEditSuccess}
                onError={handleEditError}
            />

            <Toast
                open={toast.open}
                onClose={closeToast}
                severity={toast.severity}
                text={toast?.text}
            />
        </PageWrapper>
    );
}
